import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { WriteStatus, WriteStatuses } from '../../models/issue-view';
import { IssueRowDisplay, IssueRowVariant, IssueRowVariants } from './issue-row-display';
import { IssueLocationStates } from '../../query/issue-fleet-row';
import { elapsedLabel } from '../../issue-formatting';
import { IssueSort, IssueSortKey, IssueSortKeys } from '../../query/issue-query-store';

// One Issue row, on every surface that shows one.
//
// Two element names for one component. The header is the same cells with the same widths,
// so it is this component in header mode; but it is not an Issue, and a reader of the DOM
// (a test, a screen reader walking landmarks) that takes the first app-issue-row must get
// a row. The sheet mounts the header as app-issue-column-header.
@Component({
  selector: 'app-issue-row, app-issue-column-header',
  templateUrl: './issue-row.component.html',
  styleUrls: ['./issue-row.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IssueRowComponent {
  @Input() row!: IssueRowDisplay;
  @Input() variant: IssueRowVariant = IssueRowVariants.Panel;
  // The column header row. Rendered by this component rather than by the sheet so the
  // header cells and the row cells share one stylesheet and one set of widths: two copies
  // of the widths drift, and a header a pixel out of register reads as the wrong column.
  @Input() header = false;
  @Input() sort: IssueSort | null = null;
  // Off while the sheet shows history, which the server pages in its own order.
  @Input() sortEnabled = true;
  // Whether the sheet has a location column at all. The header has no row and asks the
  // tenant; a row reads its own location state as well, and drops the cell with the column
  // so the two never disagree about where the next cell starts.
  @Input() showWhereColumn = true;
  @Output() sortChange = new EventEmitter<IssueSortKey>();

  readonly IssueSortKeys = IssueSortKeys;
  @Input() writeStatus: WriteStatus = WriteStatuses.Idle;
  // Drives the fleet list's selected state. The row stays lit while its drawer is open,
  // which is the same contract the Service Center asset drawer keeps.
  @Input() selected = false;
  // Whether the tenant has the cases feature. Two of the row's cells describe the case,
  // so both go when it is off. Off by default: a surface that forgets to answer hides
  // work tracking the tenant may not have rather than showing it.
  @Input() showCase = false;
  @Output() open = new EventEmitter<IssueRowDisplay>();

  isSorted(key: IssueSortKey): boolean { return this.sortEnabled && this.sort?.key === key; }

  sortWordKey(): string {
    return this.sort?.dir === 'asc' ? '@issuesSortedAscending' : '@issuesSortedDescending';
  }

  onSort(key: IssueSortKey): void {
    if (this.sortEnabled) this.sortChange.emit(key);
  }

  get submitting(): boolean { return this.writeStatus === WriteStatuses.Submitting; }
  get confirming(): boolean { return this.writeStatus === WriteStatuses.Confirming; }
  get isPanel(): boolean { return this.variant === IssueRowVariants.Panel; }

  // Inside one asset's screen the asset is already named by the screen, and its location
  // by the same. Repeating either spends a column to say what the reader knows.
  get showsAsset(): boolean { return !this.isPanel; }


  // Unclaimed is a real state rather than an empty cell, but only where a claim is a
  // thing that can happen at all.
  get showNoCase(): boolean { return this.showCase && this.row?.hasCase === false; }

  get ageLabel(): string { return elapsedLabel(this.row?.openedAtUtc); }

  // The VIN rides in the tooltip rather than a second line: the sheet row is one line by
  // design, and the VIN is a lookup value rather than something anyone scans.
  get assetTitle(): string {
    const name = this.row?.assetName ?? '';
    const vin = this.row?.assetVin ?? '';
    return vin ? `${name} · ${vin}` : name;
  }

  // Where the asset is. The whole column goes when the tenant flags no shop geofences: the
  // server sets the current shop only from one of those, so it can place nobody and every
  // row would read Unknown. A column of identical Unknowns is not an answer, it is a
  // column of missing data, and it costs the problem its width to say nothing.
  //
  // A tenant is all one way or the other. Where shop geofences exist an asset is at one or
  // on the road, and Unknown never appears.
  get showWhere(): boolean {
    if (this.isPanel || !this.showWhereColumn) return false;
    const state = this.row?.locationState;
    return !!state && state !== IssueLocationStates.Unknown;
  }

  // The cell names the shop when it can, because a name is what a dispatcher acts on; the
  // state is the answer when there is no name to give.
  get namesShop(): boolean {
    return this.row?.locationState === IssueLocationStates.Shop && !!this.row?.currentShopName;
  }

  get whereText(): string { return this.row?.currentShopName ?? ''; }

  // A shop whose group name has not loaded still reads as "at a shop" rather than as an
  // empty cell.
  get whereStateKey(): string {
    return this.row?.locationState === IssueLocationStates.Shop ? '@issueAtShop' : '@otr';
  }

  // The same trade for the problem: the title reads, the fingerprint identifies. Support
  // asks for the slug by name, so it stays reachable without spending row width on it.
  get problemTitle(): string {
    const name = this.row?.displayName ?? '';
    const slug = this.row?.fingerprint ?? '';
    return slug ? `${name} · ${slug}` : name;
  }

  onOpen(): void { this.open.emit(this.row); }
}
